import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CheckpointDetail,
  CheckpointSummary,
  FileChangeDetail,
  GroupActivity,
  HistoryPage,
  ProjectActivity,
} from "@ide/shared";
import { get, post } from "./api";

/** Histórico de alterações, atividade por pessoa e marcos (ADR 0006). */
export const historyKeys = {
  project: (projectId: string) => ["history", projectId] as const,
  list: (projectId: string, filter: HistoryFilter) => ["history", projectId, "list", filter] as const,
  change: (projectId: string, id: string) => ["history", projectId, "change", id] as const,
  activity: (projectId: string, days: number) => ["history", projectId, "activity", days] as const,
  checkpoints: (projectId: string) => ["history", projectId, "checkpoints"] as const,
  checkpoint: (projectId: string, n: number) => ["history", projectId, "checkpoint", n] as const,
  group: (slug: string, days: number) => ["group-activity", slug, days] as const,
};

export interface HistoryFilter {
  userId?: string;
  fileId?: string;
}

/** Linha do tempo paginada; com `live`, atualiza sozinha (alterações em andamento mudam). */
export function useHistory(projectId: string, filter: HistoryFilter, live: boolean) {
  return useInfiniteQuery({
    queryKey: historyKeys.list(projectId, filter),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: "40" });
      if (pageParam) qs.set("cursor", pageParam);
      if (filter.userId) qs.set("userId", filter.userId);
      if (filter.fileId) qs.set("fileId", filter.fileId);
      return get<HistoryPage>(`/projects/${projectId}/history?${qs}`);
    },
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: live ? 10_000 : false,
  });
}

export const useChange = (projectId: string, id: string | null) =>
  useQuery({
    queryKey: historyKeys.change(projectId, id ?? ""),
    queryFn: () => get<FileChangeDetail>(`/projects/${projectId}/history/${id}`),
    enabled: id !== null,
    // Em andamento, o diff ainda não existe: atualiza até fechar.
    refetchInterval: (q) => (q.state.data && !q.state.data.endedAt ? 10_000 : false),
  });

export const useProjectActivity = (projectId: string, days: number, live: boolean) =>
  useQuery({
    queryKey: historyKeys.activity(projectId, days),
    queryFn: () => get<ProjectActivity>(`/projects/${projectId}/activity?days=${days}`),
    refetchInterval: live ? 10_000 : false,
  });

export const useCheckpoints = (projectId: string) =>
  useQuery({
    queryKey: historyKeys.checkpoints(projectId),
    queryFn: async () =>
      (await get<{ checkpoints: CheckpointSummary[] }>(`/projects/${projectId}/checkpoints`)).checkpoints,
  });

export const useCheckpoint = (projectId: string, n: number | null) =>
  useQuery({
    queryKey: historyKeys.checkpoint(projectId, n ?? 0),
    queryFn: () => get<CheckpointDetail>(`/projects/${projectId}/checkpoints/${n}`),
    enabled: n !== null,
    staleTime: Infinity, // marcos não mudam
  });

export function useCreateCheckpoint(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (message: string) =>
      post<CheckpointSummary>(`/projects/${projectId}/checkpoints`, { message }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: historyKeys.project(projectId) }),
  });
}

export const useGroupActivity = (slug: string, days: number) =>
  useQuery({
    queryKey: historyKeys.group(slug, days),
    queryFn: () => get<GroupActivity>(`/groups/${slug}/activity?days=${days}`),
    refetchInterval: 15_000,
  });
